import React, { useState, useCallback, useEffect } from "react";
import useSWR from "swr";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CloseIcon from "@mui/icons-material/Close";
import Checkbox from "@mui/material/Checkbox";

import RejectIcon from "../../MerchantLoanRequests/icons/reject.svg";
import RestartIcon from "../svg/restart.svg";
import { fetcher } from "../../../utilities/_fetcher";

const RejectionReasonModal = ({
  onClose,
  onReasonsSubmit,
  actionType = "reject",
}) => {
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const localUserType = JSON.parse(localStorage.getItem("currUserType"));
  const userLevel = localUserType === "cs_user_layer_1" ? "l1" : "l2";

  const apiUrl = `${process.env.REACT_APP_API_URL}/api/bank-inclusion/v1/qc-reason`;

  const { data: reasonsData, error: reasonsError } = useSWR(apiUrl, fetcher);

  const loading = !reasonsData && !reasonsError;

  const reasons = reasonsData
    ? reasonsData
        .filter(
          (reason) =>
            reason.user_type === userLevel &&
            reason.reason_type === "reject" &&
            reason.is_active
        )
        .map((reason) => reason.reason)
    : [];

  const handleReasonToggle = useCallback(
    (reason) => {
      setSelectedReasons((prev) =>
        prev.includes(reason)
          ? prev.filter((r) => r !== reason)
          : [...prev, reason]
      );
    },
    [setSelectedReasons]
  );

  const handleReject = useCallback(async () => {
    if (selectedReasons.length === 0) return;

    setSubmitting(true);
    try {
      await onReasonsSubmit(selectedReasons);
    } finally {
      setSubmitting(false);
    }
  }, [selectedReasons, onReasonsSubmit]);

  const isRejectDisabled =
    selectedReasons.length === 0 || submitting || loading;

  useEffect(() => {
    const handleKeyDown = (e) => {
      e.stopPropagation();
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, []);

  if (loading) {
    return (
      <div className="approval-modal-overlay">
        <div className="loan-confirmation-modal">
          <div className="loan-confirmation-modal__header">
            <div className="loan-confirmation-modal__header-left">
              <button className="loan-confirmation-modal__back" disabled>
                <ArrowBackIosIcon fontSize="small" />
              </button>
              <h2 className="loan-confirmation-modal__title loan-confirmation-modal__title--red">
                Loading Rejection Reasons...
              </h2>
            </div>
            <button
              className="loan-confirmation-modal__close"
              onClick={onClose}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
          <div className="loan-confirmation-modal__content">
            <div className="loan-confirmation-modal__reasons">
              {[...Array(5)].map((_, index) => (
                <div key={index} className="reason-checkbox">
                  <div className="skeleton skeleton-text skeleton-text--large"></div>
                  <div className="skeleton skeleton-checkbox"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (reasonsError) {
    return (
      <div className="approval-modal-overlay">
        <div className="loan-confirmation-modal">
          <div className="loan-confirmation-modal__header">
            <div className="loan-confirmation-modal__header-left">
              <button
                className="loan-confirmation-modal__back"
                onClick={onClose}
              >
                <ArrowBackIosIcon fontSize="small" />
              </button>
              <h2 className="loan-confirmation-modal__title loan-confirmation-modal__title--red">
                Error Loading Reasons
              </h2>
            </div>
            <button
              className="loan-confirmation-modal__close"
              onClick={onClose}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
          <div className="loan-confirmation-modal__content">
            <p className="loan-confirmation-modal__message">
              Failed to load rejection reasons. Please try again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="approval-modal-overlay">
      <div className="loan-confirmation-modal">
        <div className="loan-confirmation-modal__header">
          <div className="loan-confirmation-modal__header-left">
            <button
              className="loan-confirmation-modal__back"
              onClick={onClose}
              disabled={submitting}
            >
              <ArrowBackIosIcon fontSize="small" />
            </button>
            <h2 className="loan-confirmation-modal__title loan-confirmation-modal__title--red">
              {actionType === "reject" ? "Rejection" : "Send Back"} Confirmation
            </h2>
          </div>
          <button
            className="loan-confirmation-modal__close"
            onClick={onClose}
            disabled={submitting}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="loan-confirmation-modal__content">
          <p className="loan-confirmation-modal__message">
            {actionType === "reject"
              ? "Are you sure you want to reject this bank inclusion request? Please select the reasons for this rejection."
              : "Are you sure you want to send this bank inclusion request back to level 1 QC? Please select the reasons for this."}
          </p>

          <div className="loan-confirmation-modal__reasons">
            {reasons.map((reason, index) => (
              <label key={index} className="reason-checkbox">
                <span className="reason-checkbox__text">{reason}</span>
                <Checkbox
                  checked={selectedReasons.includes(reason)}
                  onChange={() => handleReasonToggle(reason)}
                  disabled={submitting}
                  sx={{
                    color: "#e2136e",
                    "&.Mui-checked": {
                      color: "#e2136e",
                    },
                  }}
                />
              </label>
            ))}
          </div>
        </div>

        <div className="loan-confirmation-modal__footer">
          <button
            className="btn btn--reject"
            onClick={handleReject}
            disabled={isRejectDisabled}
          >
            <img
              src={actionType === "reject" ? RejectIcon : RestartIcon}
              alt={actionType === "reject" ? "Reject" : "Send Back"}
              className="btn--icon"
            />
            {submitting ? "Processing..." : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RejectionReasonModal;
